import { useQuery } from '@tanstack/react-query';
import { mostrarErro } from '@4beefopag/4bee-fopag-core/dist/molecules';
import { moneyFormat } from '@4beefopag/4bee-fopag-core/dist/util';
import { DownloadOutlined } from '@ant-design/icons';
import { Col, Input, Modal, Row, Table, message } from 'antd';
import fileDownload from 'js-file-download';
import moment from 'moment';
import 'moment/locale/pt-br';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { normalizeString, pluralize } from '../../../../services/util/string';
import { formatCpf } from '../../../../services/util/cpf';
import {
  downloadPayrollValueBandsDrillDown,
  getPayrollValueBandsDrillDown,
} from '../../../../services/repository/dashboardRepository';
import BwgButton from '../../../atoms/Button';
import CooperatorEntriesDetail from '../CooperatorEntriesDetail';
import LoadError from '../LoadError';

const { Search } = Input;

const SummaryLabel = styled.div`
  color: #8c8c8c;
  font-size: 12px;
`;

const SummaryValue = styled.div`
  font-weight: 600;
`;

export function filterCooperators(cooperators, searchTerm) {
  const normalizedSearchTerm = normalizeString(searchTerm || '');
  if (!normalizedSearchTerm) return cooperators;

  return cooperators.filter((cooperator) => {
    const nome = normalizeString(cooperator.nome || '');
    const cpf = normalizeString(cooperator.cpf || '');
    return nome.includes(normalizedSearchTerm) || cpf.includes(normalizedSearchTerm);
  });
}

export function formatCompetenceFullLabel(competencia) {
  const label = moment(competencia, 'MM/YYYY').locale('pt-BR').format('MMMM/YYYY');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const COLUMNS = [
  { title: 'CPF', dataIndex: 'cpf', key: 'cpf', render: formatCpf },
  {
    title: 'Nome do Cooperado',
    dataIndex: 'nome',
    key: 'nome',
    sorter: (a, b) => a.nome.localeCompare(b.nome),
  },
  {
    title: 'Valor',
    dataIndex: 'valorConsolidado',
    key: 'valorConsolidado',
    align: 'right',
    sorter: (a, b) => a.valorConsolidado - b.valorConsolidado,
    render: (value) => moneyFormat(value),
  },
];

function ValueBandsDrillDownModal({ open, onClose, folhaIds, competencia, faixaId, bandLabel }) {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['payroll-value-bands-drill-down', folhaIds, competencia, faixaId],
    queryFn: () => getPayrollValueBandsDrillDown({ folhaIds, competencia, faixaId }),
    enabled: open && !!competencia && !!faixaId,
  });

  const cooperators = data?.cooperados ?? [];
  const filteredCooperators = useMemo(() => filterCooperators(cooperators, searchTerm), [cooperators, searchTerm]);
  const totalDistributedValue = useMemo(
    () => cooperators.reduce((sum, cooperator) => sum + cooperator.valorConsolidado, 0),
    [cooperators]
  );

  // A busca vive neste componente (fora do conteúdo do Modal), então o destroyOnClose não a limpa
  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const handleExportClick = async () => {
    const hide = message.loading('Seu download está sendo preparado');
    try {
      const worksheet = await downloadPayrollValueBandsDrillDown({ folhaIds, competencia, faixaId, busca: searchTerm });
      fileDownload(worksheet, `detalhamento-faixa-remuneracao-${competencia.replace('/', '-')}.xlsx`);
    } catch (e) {
      mostrarErro(e);
    } finally {
      hide();
    }
  };

  return (
    <Modal
      title="Detalhamento — Quantidade Cooperados por Faixa de Remuneração"
      open={open}
      onCancel={handleClose}
      // Desmonta a tabela ao fechar: sem isso, as linhas expandidas mantêm a query de lançamentos ativa
      // e o refetchOnWindowFocus volta a consultar o CPF depois que o usuário saiu do detalhe
      destroyOnClose
      width={960}
      footer={[
        <BwgButton
          key="fechar"
          onClick={handleClose}
        >
          Fechar
        </BwgButton>,
        <BwgButton
          key="exportar"
          icon={<DownloadOutlined />}
          disabled={isPending || isError}
          onClick={handleExportClick}
        >
          Exportar Excel
        </BwgButton>,
      ]}
    >
      <Row
        gutter={16}
        style={{ marginBottom: 16 }}
      >
        <Col span={6}>
          <SummaryLabel>Competência</SummaryLabel>
          <SummaryValue>{competencia ? formatCompetenceFullLabel(competencia) : '-'}</SummaryValue>
        </Col>
        <Col span={6}>
          <SummaryLabel>Faixa</SummaryLabel>
          <SummaryValue>{bandLabel ?? '-'}</SummaryValue>
        </Col>
        <Col span={6}>
          <SummaryLabel>Quantidade de Cooperados</SummaryLabel>
          <SummaryValue>{isError ? '-' : pluralize(cooperators.length, 'cooperado', 'cooperados')}</SummaryValue>
        </Col>
        <Col span={6}>
          <SummaryLabel>Valor Total Distribuído</SummaryLabel>
          <SummaryValue>{isError ? '-' : moneyFormat(totalDistributedValue)}</SummaryValue>
        </Col>
      </Row>

      <Search
        placeholder="Buscar por CPF ou nome..."
        allowClear
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      {isError ? (
        <LoadError
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Table
          loading={isPending}
          columns={COLUMNS}
          dataSource={filteredCooperators}
          rowKey="cpf"
          expandable={{
            expandedRowRender: (record) => (
              <CooperatorEntriesDetail
                cpf={record.cpf}
                competencia={competencia}
                folhaIds={folhaIds}
              />
            ),
          }}
        />
      )}
    </Modal>
  );
}

ValueBandsDrillDownModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folhaIds: PropTypes.arrayOf(PropTypes.number),
  competencia: PropTypes.string,
  faixaId: PropTypes.string,
  bandLabel: PropTypes.string,
};

ValueBandsDrillDownModal.defaultProps = {
  folhaIds: undefined,
  competencia: undefined,
  faixaId: undefined,
  bandLabel: undefined,
};

export default ValueBandsDrillDownModal;
